import { useEffect, useRef, useState } from "react";
import { Loader2, Pencil } from "lucide-react";

/**
 * Inline-editable ad title shared by My Ads and Ads Express (standard + KC).
 *
 * Shows the resolved display name with a pencil affordance; clicking either
 * swaps in a text input. Enter or blur saves (trimmed, max 200 chars), Escape
 * cancels. Saving an empty string clears the custom name so the title falls
 * back to the auto-generated one.
 */
export function InlineRenameTitle({
  customName,
  fallbackName,
  onSave,
  saving = false,
  className = "",
  inputClassName = "",
  testId,
}: {
  /** Current user-set name (null/undefined = none). */
  customName: string | null | undefined;
  /** Auto-generated title to fall back to (and shown when clearing). */
  fallbackName: string;
  /** Called with the new name, or null to clear back to the fallback. */
  onSave: (name: string | null) => void | Promise<void>;
  saving?: boolean;
  className?: string;
  inputClassName?: string;
  testId: string;
}) {
  const [failedDraft, setFailedDraft] = useState<{ value: string | null } | null>(null);
  const displayName = failedDraft
    ? failedDraft.value?.trim() || fallbackName
    : customName?.trim() || fallbackName;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  // Guards the blur handler after Enter/Escape already resolved the edit.
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const startEdit = () => {
    if (saving) return;
    resolvedRef.current = false;
    setDraft(failedDraft ? failedDraft.value?.trim() || fallbackName : customName?.trim() || fallbackName);
    setEditing(true);
  };

  const commit = async () => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    setEditing(false);
    const next = draft.trim().slice(0, 200);
    const current = customName?.trim() || "";
    // No-op cases: unchanged custom name, or no custom name and the draft
    // still matches the auto title.
    if (next === current) return;
    if (!current && next === fallbackName) return;
    try {
      await onSave(next || null);
      setFailedDraft(null);
    } catch {
      // Keep the attempted name visible without automatically refocusing the
      // input; refocusing can blur another failed editor and create a retry loop.
      setFailedDraft({ value: next || null });
    }
  };

  const cancel = () => {
    resolvedRef.current = true;
    setEditing(false);
    setFailedDraft(null);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        maxLength={200}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            cancel();
          }
        }}
        onClick={(e) => e.stopPropagation()}
        className={`w-full bg-background border border-input rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-ring ${inputClassName}`}
        data-testid={`input-${testId}`}
        aria-label="Ad name"
      />
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        startEdit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          startEdit();
        }
      }}
      className={`group/rename inline-flex items-start gap-1 max-w-full cursor-text text-left ${className}`}
      title="Click to rename"
      data-testid={`text-${testId}`}
    >
      <span className="min-w-0">{displayName}</span>
      {saving ? (
        <Loader2 className="h-3 w-3 shrink-0 mt-0.5 animate-spin text-muted-foreground" />
      ) : (
        <Pencil className="h-3 w-3 shrink-0 mt-0.5 text-muted-foreground opacity-0 group-hover/rename:opacity-100 transition-opacity" />
      )}
    </span>
  );
}
